import {
  DeliverableGroupKey,
  FeatureKey,
  FeatureLaneModelWithStatus,
  FeatureLaneSurveyStatus,
  FeatureLaneUserSurveyDatabaseModel,
  UserSurveyDatabaseModel,
  UserSurveyDefinitionModelForFeatureLane
} from "data/models/feature-lane";
import {getAuth} from "firebase/auth";
import {useAuthState} from "react-firebase-hooks/auth";
import {useObjectVal} from "react-firebase-hooks/database";
import {ref} from "firebase/database";
import {database} from "hooks/firebase";
import {SURVEY_ID_TO_USER_SURVEY} from "./surveys";
import {isEmpty} from "helpers/is-empty";
import {isNullOrUndefined} from "helpers/is-null-or-undefined";
import {joinStringsWithCommaAnd} from "helpers/join-strings-with-and";
import {SurveyKey} from "data/models/survey/keys";
import {THE_SINGLE_FEATURE_LANE} from "data/definition/feature-lanes/canva-assets";
import {DELIVERABLES_FEATURE_LANE} from "data/definition/feature-lanes/deliverables";

type UserSurveyCompleted = { [key in SurveyKey]: boolean };
type UserSurveyStatusWithoutDependencies = { [key in SurveyKey]: FeatureLaneSurveyStatus };
type UserSurveyMap = { [key in SurveyKey]: UserSurveyDefinitionModelForFeatureLane };

export function getFeatureLaneDefinitions(deliverableGroupId: DeliverableGroupKey) {
  return  {
    "canva-assets": THE_SINGLE_FEATURE_LANE,
    "deliverables": DELIVERABLES_FEATURE_LANE,
  }[deliverableGroupId];
}

export function useFeatureLane(deliverableGroupId: DeliverableGroupKey | undefined):
    [FeatureLaneModelWithStatus[] | null, boolean, Error | null] {
  const auth = getAuth();
  const [user, userLoading, userError] = useAuthState(auth);
  const [userSurveysValues, userSurveysValuesLoading, userSurveysValuesError] =
      useObjectVal<UserSurveyDatabaseModel, DeliverableGroupKey>((user && ref(database, `user-data/${user.uid}/user-surveys`)) ?? null);

  if (deliverableGroupId === undefined) {
    return [null, userLoading || userSurveysValuesLoading, userError ?? userSurveysValuesError ?? null];
  }

  const featureLaneDefinitions = getFeatureLaneDefinitions(deliverableGroupId);

  const flattenSurveyValues = isNullOrUndefined(userSurveysValues) ? null : (
      (Object.keys(userSurveysValues) as DeliverableGroupKey[])
          .map(_deliverableGroupId =>
              (Object.keys(userSurveysValues[_deliverableGroupId]) as FeatureKey[])
                  .map(featureLaneId =>
                      (Object.keys(userSurveysValues[_deliverableGroupId][featureLaneId]) as SurveyKey[])
                          .map(surveyId => ({
                                [surveyId]:
                                    userSurveysValues[_deliverableGroupId][featureLaneId][surveyId]
                              }
                          )))).flat().flat()
          .reduce((result, value) => ({
            ...result,
            ...value,
          }), {}) as FeatureLaneUserSurveyDatabaseModel
  );

  const allFeatureLaneDefinitions = [...THE_SINGLE_FEATURE_LANE, ...DELIVERABLES_FEATURE_LANE];

  const featureLaneSurveyCompleteness: UserSurveyCompleted = allFeatureLaneDefinitions
      .map(featureLaneDefinition =>
          featureLaneDefinition.userSurveys.map((userSurvey) => {
                const featureLaneSurveyValues = flattenSurveyValues?.[userSurvey.id];
                const userSurveyDefinition = SURVEY_ID_TO_USER_SURVEY[userSurvey.id];
                const allSet = featureLaneSurveyValues !== undefined && userSurveyDefinition.surveyQuestions.reduce(
                    (result, question) => (
                        result
                        && (!question.required || !isEmpty(featureLaneSurveyValues?.[question.id]))
                    ),
                    true
                );
                return {
                  [userSurvey.id]: allSet,
                };
              }
          )
      ).flat()
      .reduce((result, userSurvey) => ({
        ...result,
        ...userSurvey,
      }), {}) as UserSurveyCompleted;

  function userSurveyStatus(
      userSurveyForFeatureLane: UserSurveyDefinitionModelForFeatureLane,
  ): FeatureLaneSurveyStatus {
    if (!userSurveyForFeatureLane.rules.allowsAnonymous && isNullOrUndefined(user)) {
      return {
        status: 'blocked',
        statusReason: 'Start by filling in "Business Information" or "Sign In"',
        blockedBy: ["business-info"],
      };
    }
    if (featureLaneSurveyCompleteness[userSurveyForFeatureLane.id]) {
      return {
        status: 'finished',
        statusReason: null,
        blockedBy: null,
      };
    }
    return {
      status: 'in progress',
      statusReason: null,
      blockedBy: null,
    };
  }

  const featureLaneUserSurveysMap: UserSurveyMap = allFeatureLaneDefinitions.map(featureLaneDefinition =>
      featureLaneDefinition.userSurveys.map(userSurvey => ({
        [userSurvey.id]: userSurvey,
      }))
  ).flat()
      .reduce((result, userSurvey) => ({
        ...result,
        ...userSurvey,
      })) as UserSurveyMap;


  const featureLaneUserSurveysStatusWithoutDependencies: UserSurveyStatusWithoutDependencies = allFeatureLaneDefinitions.map(featureLaneDefinition =>
      featureLaneDefinition.userSurveys.map(userSurvey => ({
        [userSurvey.id]: userSurveyStatus(userSurvey),
      }))
  ).flat()
      .reduce((result, userSureyStatusWithoutDependencies) => ({
        ...result,
        ...userSureyStatusWithoutDependencies,
      })) as UserSurveyStatusWithoutDependencies;

  function hasUserSurveyDependencies(userSurvey: UserSurveyDefinitionModelForFeatureLane): UserSurveyDefinitionModelForFeatureLane [] {
    return userSurvey.rules.dependsOn
        .filter(userSurveyId =>
            featureLaneUserSurveysStatusWithoutDependencies[userSurveyId].status !== 'finished'
        ).map(userSurveyId => featureLaneUserSurveysMap[userSurveyId]);
  }

  function userSurveyStatusWithDependencies(
      userSurvey: UserSurveyDefinitionModelForFeatureLane,
      userSurveyStatusWithoutDependencies: FeatureLaneSurveyStatus,
  ): FeatureLaneSurveyStatus {
    if (userSurveyStatusWithoutDependencies.status === 'blocked') {
      return userSurveyStatusWithoutDependencies;
    }
    const dependencies = hasUserSurveyDependencies(userSurvey);
    return {
      status: dependencies.length > 0 ? 'blocked' : userSurveyStatusWithoutDependencies.status,
      statusReason: dependencies.length > 0 ? `Unavailable, finish ${joinStringsWithCommaAnd(dependencies.map(d => d.title))} first.` : userSurveyStatusWithoutDependencies.statusReason,
      blockedBy: dependencies.length > 0 ? dependencies.map(d => d.id) : null,
    }
  }

  const featureLaneUserSurveysStatus: FeatureLaneModelWithStatus[] = featureLaneDefinitions.map((featureLaneDefinition) => ({
    ...featureLaneDefinition,
    userSurveys: featureLaneDefinition.userSurveys.map(userSurvey => ({
      ...userSurvey,
      ...userSurveyStatusWithDependencies(
          userSurvey,
          featureLaneUserSurveysStatusWithoutDependencies[userSurvey.id],
      ),
    }))
  }));

  return [featureLaneUserSurveysStatus, userLoading || userSurveysValuesLoading, userError ?? userSurveysValuesError ?? null];
}
